import React, { useState, useEffect } from 'react';

// --- Icons (Simulated Lucide React) ---
const FileTextIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><line x1="10" y1="9" x2="8" y2="9"/></svg>;
const PlusIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14M5 12h14"/></svg>;
const EditIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5L2 22l1.5-5.5L17 3z"/></svg>;
const TrashIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>;
const SaveIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/></svg>;
const TargetIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>;
const AlertCircleIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
const ListOrderedIcon = (props) => <svg {...props} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="10" y1="6" x2="21" y2="6"/><line x1="10" y1="12" x2="21" y2="12"/><line x1="10" y1="18" x2="21" y2="18"/><path d="M4 6h1a1 1 0 0 0 0-2H4a1 1 0 0 0 0 2zm0 6h1a1 1 0 0 0 0-2H4a1 1 0 0 0 0 2zm0 6h1a1 1 0 0 0 0-2H4a1 1 0 0 0 0 2z"/></svg>;


// --- Constants & Initial Data ---
const PRIMARY_COLOR = 'indigo-600';
const SECONDARY_COLOR = 'purple-600';

const INITIAL_RULES = [
    { id: 101, name: 'India: FTE (Junior Tech)', targetRoles: ['Software Engineer I', 'Data Analyst'], templateName: 'IN-Tech-FTE-001', effectiveDate: '2024-01-01', status: 'Active', terms: 'Standard 90-day probation period, 30 days notice.' },
    { id: 102, name: 'USA: Internship Program', targetRoles: ['Summer Intern'], templateName: 'US-Intern-Temp-002', effectiveDate: '2024-05-15', status: 'Active', terms: 'Fixed 3-month term, hourly payment structure.' },
    { id: 103, name: 'India: Senior Leadership (Sales)', targetRoles: ['Sales Director', 'VP of Business Dev'], templateName: 'IN-Exec-Sales-003', effectiveDate: '2023-10-01', status: 'Inactive', terms: 'Includes stock options and annual performance bonus clause.' },
];

let nextId = 104;

/**
 * Rule Configuration Panel (Right Side)
 */
const RuleConfigPanel = ({ selectedRule, onUpdateRule, onAddRule, onSelectRule }) => {
    const isNew = selectedRule && !selectedRule.id;
    const isEditing = !!selectedRule;

    const [formData, setFormData] = useState(
        selectedRule || { name: '', targetRoles: [], templateName: '', effectiveDate: new Date().toISOString().substring(0, 10), status: 'Active', terms: '' }
    );

    useEffect(() => {
        setFormData(selectedRule && selectedRule.id 
            ? selectedRule 
            : { name: '', targetRoles: [], templateName: '', effectiveDate: new Date().toISOString().substring(0, 10), status: 'Active', terms: '' }
        );
    }, [selectedRule]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'targetRolesText') {
            // Update the temporary text input for roles
            setFormData(prev => ({ ...prev, targetRolesText: value }));
            // Convert comma-separated string back to array of trimmed strings
            const rolesArray = value.split(',').map(role => role.trim()).filter(role => role.length > 0);
            setFormData(prev => ({ ...prev, targetRoles: rolesArray }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: type === 'checkbox' ? (checked ? 'Active' : 'Inactive') : value,
            }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const ruleToSave = {
            ...formData,
            name: formData.name.trim(),
            // Ensure targetRoles is saved as an array
            targetRoles: formData.targetRolesText 
                ? formData.targetRolesText.split(',').map(role => role.trim()).filter(role => role.length > 0)
                : formData.targetRoles,
        };
        // Remove the temporary text field before saving
        delete ruleToSave.targetRolesText;

        if (isNew) {
            onAddRule(ruleToSave);
        } else {
            onUpdateRule(ruleToSave);
        }
        onSelectRule(null);
    };
    
    // For initializing the target roles textarea
    const initialRolesText = (formData.targetRoles || []).join(', ');
    
    if (!isEditing) {
        return (
            <div className="p-8 h-full flex flex-col items-center justify-center text-center bg-gray-50 rounded-xl border border-dashed border-gray-300">
                <FileTextIcon className="w-10 h-10 text-gray-400 mb-3" />
                <p className="text-lg font-poppins text-gray-700 font-medium">Rule Configuration</p>
                <p className="text-sm text-gray-500 font-roboto mt-1">
                    Select a rule to view details, or use the **+ New Rule** button.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
            <h3 className="text-xl font-poppins font-semibold text-gray-800 border-b pb-3 mb-4">
                {isNew ? 'Define New Offer Rule' : `Editing: ${formData.name}`}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
                {/* Rule Name */}
                <div>
                    <label htmlFor="name" className="advanced-label">Rule Name *</label>
                    <input
                        id="name"
                        name="name"
                        type="text"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="advanced-input"
                        placeholder="e.g., India FTE - Junior Level"
                    />
                </div>

                {/* Template ID & Effective Date */}
                <div className="grid grid-cols-2 gap-4">
                    <div>
                        <label htmlFor="templateName" className="advanced-label">Template ID / Reference *</label>
                        <input
                            id="templateName"
                            name="templateName"
                            type="text"
                            value={formData.templateName}
                            onChange={handleChange}
                            required
                            className="advanced-input"
                            placeholder="e.g., IN-Tech-FTE-001"
                        />
                    </div>
                    <div>
                        <label htmlFor="effectiveDate" className="advanced-label">Effective Date *</label>
                        <input
                            id="effectiveDate"
                            name="effectiveDate"
                            type="date"
                            value={formData.effectiveDate}
                            onChange={handleChange}
                            required
                            className="advanced-input"
                        />
                    </div>
                </div>

                {/* Target Roles */}
                <div>
                    <label htmlFor="targetRolesText" className="advanced-label">Applicable Job Titles (Comma Separated)</label>
                    <textarea
                        id="targetRolesText"
                        name="targetRolesText"
                        value={formData.targetRolesText !== undefined ? formData.targetRolesText : initialRolesText}
                        onChange={handleChange}
                        rows="2"
                        className="advanced-input"
                        placeholder="e.g., Software Engineer I, Data Analyst, Product Manager"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                        Leave blank to apply to all jobs.
                    </p>
                </div>
                
                {/* Status & Terms */}
                <div className="pt-2">
                    <label htmlFor="status" className="advanced-label flex items-center">
                        <input
                            id="status"
                            name="status"
                            type="checkbox"
                            checked={formData.status === 'Active'}
                            onChange={handleChange}
                            className={`w-4 h-4 text-${PRIMARY_COLOR} border-gray-300 rounded focus:ring-${PRIMARY_COLOR} mr-2`}
                        />
                        Rule is Active
                    </label>
                </div>

                <div>
                    <label htmlFor="terms" className="advanced-label">Terms & Conditions Summary</label>
                    <textarea
                        id="terms"
                        name="terms"
                        value={formData.terms}
                        onChange={handleChange}
                        rows="3"
                        className="advanced-input"
                        placeholder="Define key terms like probation, notice period, and bonus structure summary."
                    />
                </div>

                {/* Save Button */}
                <div className="pt-4 flex justify-end space-x-3">
                    <button
                        type="button"
                        onClick={() => onSelectRule(null)}
                        className="px-5 py-2 text-sm font-roboto font-medium rounded-lg text-gray-700 hover:bg-gray-100 transition duration-150"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className={`px-5 py-2 text-sm font-roboto font-medium rounded-lg text-white shadow-md flex items-center space-x-1 transition duration-150
                            bg-${SECONDARY_COLOR} hover:bg-purple-700 disabled:bg-gray-400
                        `}
                        disabled={!formData.name.trim() || !formData.templateName.trim()}
                    >
                        <SaveIcon className="w-4 h-4" />
                        <span>{isNew ? 'Create Rule' : 'Save Changes'}</span>
                    </button>
                </div>
            </form>
        </div>
    );
};


/**
 * Individual Rule Card Component (Left Side List)
 */
const RuleCard = ({ rule, isSelected, onSelect, onDelete }) => {
    const statusColor = rule.status === 'Active' ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
    const targetsCount = rule.targetRoles.length;
    
    return (
        <div
            className={`p-4 rounded-xl cursor-pointer transition duration-150 round-card-focus
                ${isSelected 
                    ? `bg-indigo-50 ring-2 ring-indigo-600 border border-indigo-600 shadow-md` 
                    : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'}
            `}
            tabIndex={0} 
            role="button"
            onClick={() => onSelect(rule)}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(rule);
                }
            }}
        >
            <div className="flex justify-between items-start">
                {/* Left: Details */}
                <div className="flex-1 min-w-0 pr-4">
                    <div className="flex items-center space-x-3">
                        <span className={`text-lg font-poppins font-semibold truncate ${isSelected ? 'text-gray-900' : 'text-gray-800'}`}>
                            {rule.name}
                        </span>
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${statusColor}`}>
                            {rule.status}
                        </span>
                    </div>

                    <div className={`text-sm mt-1 ${isSelected ? 'text-gray-700' : 'text-gray-500'} font-medium flex items-center space-x-4`}>
                        <div className='flex items-center'>
                            <ListOrderedIcon className="w-3.5 h-3.5 mr-1" />
                            <span className="font-semibold">{rule.templateName}</span>
                        </div>
                        <div className='flex items-center'>
                            <TargetIcon className="w-3.5 h-3.5 mr-1" />
                            <span className="font-semibold">{targetsCount > 0 ? `${targetsCount} Roles` : 'All Roles'}</span>
                        </div>
                    </div>
                    
                    <p className={`text-sm mt-1 truncate ${isSelected ? 'text-gray-600' : 'text-gray-500'} font-roboto`}>
                        <span className="font-medium mr-1">Terms:</span> {rule.terms || 'No key terms defined.'}
                    </p>
                </div>

                {/* Right: Actions */}
                <div className="flex space-x-2 flex-shrink-0 pt-1">
                    <button
                        onClick={(e) => { e.stopPropagation(); onSelect(rule); }}
                        className="p-1.5 text-gray-500 hover:text-purple-600 rounded-full hover:bg-purple-100 transition focus:outline-none focus:ring-2 focus:ring-purple-400"
                        aria-label={`Edit ${rule.name}`}
                    >
                        <EditIcon />
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onDelete(rule.id); }}
                        className="p-1.5 text-gray-500 hover:text-red-600 rounded-full hover:bg-red-100 transition focus:outline-none focus:ring-2 focus:ring-red-400"
                        aria-label={`Delete ${rule.name}`}
                    >
                        <TrashIcon />
                    </button>
                </div>
            </div>
        </div>
    );
};


/**
 * Main Offer Letter Rules Setup Component
 */
const OfferLetterRulesSetup = () => {
    const [rules, setRules] = useState(INITIAL_RULES);
    const [selectedRuleId, setSelectedRuleId] = useState(null);
    
    const selectedRule = rules.find(r => r.id === selectedRuleId) || null;

    const handleAddRule = (newRuleData) => {
        const newRule = { ...newRuleData, id: nextId++, targetRoles: newRuleData.targetRoles || [] };
        setRules([...rules, newRule]);
    };

    const handleUpdateRule = (updatedRule) => {
        setRules(rules.map(r => r.id === updatedRule.id ? updatedRule : r));
    };

    const handleDeleteRule = (id) => {
        if (window.confirm('Are you sure you want to delete this offer letter rule?')) {
            setRules(rules.filter(r => r.id !== id));
            if (selectedRuleId === id) {
                setSelectedRuleId(null);
            }
        }
    };

    const handleNewRuleClick = () => {
        setSelectedRuleId('new');
    };

    const handleSelectRule = (rule) => {
        setSelectedRuleId(rule?.id || null);
    };
    
    // Determine the rule object to pass to the config panel
    const configPanelRule = selectedRuleId === 'new' 
        ? { id: null, name: '', targetRoles: [], templateName: '', effectiveDate: new Date().toISOString().substring(0, 10), status: 'Active', terms: '', targetRolesText: '' } 
        : selectedRule;

    return (
        <div className="min-h-screen bg-gray-50 flex items-start justify-center p-6 sm:p-8 font-roboto">
            {/* Global Font and Advanced Input/Label Styles */}
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto:wght@400;500;700&display=swap');
                    .font-poppins {
                        font-family: 'Poppins', sans-serif;
                    }
                    .font-roboto {
                        font-family: 'Roboto', sans-serif;
                    }
                    /* Custom colors matching the visual style */
                    .bg-indigo-600 { background-color: #4f46e5; }
                    .hover\\:bg-indigo-700:hover { background-color: #4338ca; }
                    .text-indigo-600 { color: #4f46e5; }
                    .ring-indigo-600 { --tw-ring-color: #4f46e5; }

                    .bg-purple-600 { background-color: #9333ea; }
                    .hover\\:bg-purple-700:hover { background-color: #7e22ce; }
                    .text-purple-600 { color: #9333ea; }

                    /* --- GLOBAL FOCUS FIX (Addressing the previous issue) --- */
                    /* Removes default focus on all interactive elements */
                    *:focus {
                        outline: none !important;
                    }
                    /* Applies custom, styled focus ring only when needed (e.g., keyboard navigation) */
                    .advanced-input:focus-visible,
                    .round-card-focus:focus-visible,
                    button:focus-visible {
                        outline: 2px solid transparent;
                        outline-offset: 2px;
                        border-color: #6366f1; /* indigo-500 */
                        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.4) !important; /* Custom focus ring */
                    }
                    
                    /* Advanced Input/Label Styles */
                    .advanced-label {
                        display: block;
                        font-size: 0.875rem; /* text-sm */
                        font-weight: 500; /* font-medium */
                        color: #374151; /* text-gray-700 */
                        margin-bottom: 0.25rem;
                        font-family: 'Roboto', sans-serif;
                    }
                    .advanced-input {
                        width: 100%;
                        padding: 0.65rem 0.75rem; /* Increased padding */
                        border: 1px solid #d1d5db; /* border-gray-300 */
                        border-radius: 0.5rem; /* rounded-lg */
                        box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05); /* shadow-sm */
                        font-size: 0.875rem; /* text-sm */
                        font-family: 'Roboto', sans-serif;
                        transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
                        -webkit-appearance: none;
                        -moz-appearance: none;
                        appearance: none;
                    }
                    /* Fix for date input on some browsers */
                    input[type="date"]::-webkit-calendar-picker-indicator {
                        filter: invert(0.5);
                    }
                `}
            </style>

            <div className="max-w-7xl w-full bg-white rounded-xl shadow-2xl border border-gray-100 mt-4 overflow-hidden">
                {/* Header */}
                <div className="p-6 border-b border-gray-100 bg-white flex justify-between items-center">
                    <div>
                        <h1 className="text-3xl font-poppins font-semibold text-gray-800 flex items-center">
                            <FileTextIcon className={`w-7 h-7 mr-3 text-indigo-600`} />
                            Offer Letter Rules Setup
                        </h1>
                        <p className="text-sm text-gray-500 mt-1 font-roboto">
                            Define and manage the criteria for which offer letter template is used for candidates.
                        </p>
                    </div>
                    <div className="flex space-x-3">
                        <button className={`px-4 py-2 text-sm font-roboto font-medium rounded-lg text-white shadow-md bg-indigo-600 hover:bg-indigo-700 flex items-center space-x-1 transition`}>
                            <SaveIcon className="w-4 h-4" />
                            <span>Publish Rules</span>
                        </button>
                        <button
                            onClick={handleNewRuleClick}
                            className={`px-4 py-2 text-sm font-roboto font-medium rounded-lg text-white shadow-md bg-green-500 hover:bg-green-600 flex items-center space-x-1 transition`}
                        >
                            <PlusIcon className="w-4 h-4" />
                            <span>New Rule</span>
                        </button>
                    </div>
                </div>

                {/* Main Content: Two Columns */}
                <div className="grid grid-cols-1 lg:grid-cols-3 divide-x divide-gray-100">
                    {/* Left Column: Rules List */}
                    <div className="col-span-2 p-6 space-y-4">
                        <div className="flex justify-between items-center text-gray-600 text-sm font-medium border-b pb-2">
                            <h2 className="text-lg font-poppins font-medium text-gray-700">Rules ({rules.length})</h2>
                            <span className="text-xs text-gray-500">First rule matched is applied</span>
                        </div>

                        {rules.length > 0 ? (
                            <div className="space-y-3">
                                {rules.map((rule) => (
                                    <RuleCard
                                        key={rule.id}
                                        rule={rule}
                                        isSelected={rule.id === selectedRuleId}
                                        onSelect={handleSelectRule}
                                        onDelete={handleDeleteRule}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div className="text-center p-12 bg-white border border-dashed border-gray-200 rounded-xl">
                                <AlertCircleIcon className="w-8 h-8 mx-auto text-red-500 mb-3" />
                                <p className="text-md font-poppins text-gray-700 mb-1 font-semibold">
                                    No Offer Rules Defined
                                </p>
                                <p className="text-sm text-gray-500 font-roboto">
                                    Offers will default to a basic template. Click '+ New Rule' to create a customized rule.
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Right Column: Configuration Panel */}
                    <div className="col-span-1 p-6 space-y-6 bg-gray-50">
                        <RuleConfigPanel
                            selectedRule={configPanelRule}
                            onUpdateRule={handleUpdateRule}
                            onAddRule={handleAddRule}
                            onSelectRule={handleSelectRule}
                        />

                        {/* Additional Information Block */}
                        <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                            <h3 className="text-lg font-poppins font-semibold text-gray-800 border-b pb-3 mb-4 flex items-center">
                                <TargetIcon className="w-4 h-4 mr-2 text-purple-600" />
                                Rule Matching Logic
                            </h3>
                            <ul className="text-sm text-gray-600 font-roboto list-disc pl-5 space-y-2">
                                <li>Rules are executed in the order they appear.</li>
                                <li>The **first Active rule** that matches the candidate's job title will be applied.</li>
                                <li>If no rule matches, the system default template will be used.</li>
                            </ul>
                            <button className="mt-4 w-full py-2 text-sm font-roboto font-medium rounded-lg text-indigo-700 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100 transition">
                                Manage Default Template
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OfferLetterRulesSetup;